import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../../components/Logo';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { GlobeIcon } from '../../components/common/SvgIcons';
import './WebsiteLogin.css'; // Reuse the same styles

const ClientRegister = () => {
  const navigate = useNavigate();
  const { registerClient, setUser } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    factoryName: '',
    address: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate required fields
    if (!formData.fullName.trim()) {
      setError(t('register.fullNamePlaceholder', 'Full name is required'));
      return;
    }
    if (!formData.phone.trim()) {
      setError(t('register.phonePlaceholder', 'Phone number is required'));
      return;
    }

    // Validate phone format (basic validation)
    const phoneRegex = /^[\d\s\-+()]+$/;
    if (!phoneRegex.test(formData.phone)) {
      setError(t('validation.phoneInvalid', 'Please enter a valid phone number'));
      return;
    }

    // Validate email format if provided
    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
      setError(t('validation.emailInvalid', 'Please enter a valid email address'));
      return;
    }

    setLoading(true);

    try {
      console.log('📝 Registering client (phone-only, no password)');
      const response = await registerClient(formData);
      
      if (response.success) {
        if (response.data) {
          setUser(response.data);
        }
        console.log('✅ Client registration successful, redirecting to client portal');
        navigate('/client-portal');
      }
    } catch (err) {
      console.error('❌ Registration error:', err);
      const errorMessage = err.response?.data?.message || t('msg.error', 'Registration failed');
      
      // Provide helpful error messages
      if (errorMessage.includes('already registered') || errorMessage.includes('already exists')) {
        setError(t('register.alreadyRegistered', 'This phone number is already registered. Please try logging in instead.'));
      } else if (errorMessage.includes('validation failed')) {
        setError(t('msg.checkFields', 'Please check all fields and try again'));
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="website-login-container">
      <div className="website-login-wrapper">
        <div className="login-header">
          <div className="flex items-center justify-between mb-4">
            <Link to="/login" className="back-link">
              {t('register.backToLogin', '← Back to Login')}
            </Link>
            <button
              type="button"
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-bold transition shadow-xs"
              style={{
                background: 'rgba(var(--theme-primary-rgb, 5, 150, 105), 0.08)',
                borderColor: 'rgba(var(--theme-primary-rgb, 5, 150, 105), 0.35)',
                color: 'var(--theme-primary, #059669)',
                cursor: 'pointer'
              }}
              title={language === 'ar' ? 'Switch to English' : 'التحويل للغة العربية'}
            >
              <GlobeIcon className="w-3.5 h-3.5" style={{ color: 'var(--theme-primary, #059669)' }} />
              <span>{language === 'ar' ? 'English' : 'العربية'}</span>
            </button>
          </div>
          <div className="login-logo">
            <Logo variant="full" size="medium" alt={t('appName', 'Sendroli Group')} />
          </div>
          <h1>{t('register.title', 'Create Account')}</h1>
          <p>{t('register.subtitle', 'Register to manage your orders')}</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-alert">{error}</div>}

          <div className="info-box">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
            <p>{t('register.noPasswordNotice', 'No password required! Simply log in with your phone number.')}</p>
          </div>

          <div className="form-group">
            <label htmlFor="fullName">{t('register.fullName', 'Full Name *')}</label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder={t('register.fullNamePlaceholder', 'Enter your full name')}
              required
              autoComplete="name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">{t('register.phone', 'Phone Number *')}</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder={t('register.phonePlaceholder', '+1234567890 or 0123456789')}
              required
              autoComplete="tel"
            />
            <small className="form-hint">{t('register.phoneHint', 'Use this phone number to log in later')}</small>
          </div>

          <div className="form-group">
            <label htmlFor="email">{t('register.email', 'Email (Optional)')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="factoryName">{t('register.factoryName', 'Factory Name (Optional)')}</label>
            <input
              type="text"
              id="factoryName"
              name="factoryName"
              value={formData.factoryName}
              onChange={handleChange}
              placeholder={t('clients.factoryName', 'Your factory or business name')}
              autoComplete="organization"
            />
          </div>

          <div className="form-group">
            <label htmlFor="address">{t('register.address', 'Address (Optional)')}</label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder={t('clients.address', 'Your business address')}
              autoComplete="address-line1"
            />
          </div>

          <button 
            type="submit" 
            className="login-btn"
            disabled={loading}
          >
            {loading ? t('register.submitting', 'Creating Account...') : t('register.submit', 'Create Account')}
          </button>
        </form>

        <div className="login-footer">
          <p className="register-text">
            {t('register.alreadyHaveAccount', 'Already have an account?')} <Link to="/login" className="register-link">{t('register.signInLink', 'Sign in')}</Link>
          </p>
          <p className="login-info">
            {t('register.noPasswordNotice', 'Passwordless login - just use your phone number to access your account.')}
          </p>
        </div>
      </div>

      <div className="login-background">
        <div className="gradient-overlay"></div>
      </div>
    </div>
  );
};

export default ClientRegister;
